import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { BsThreeDotsVertical } from "react-icons/bs";

/**
 * The top bar's module links. Modules that do not fit the bar's width move into a
 * three-dots dropdown at its end instead of being squeezed or clipped, so every module a
 * user can open stays reachable on a narrow screen.
 *
 * Widths are read from a hidden copy of the buttons rendered with the same classes, so
 * the split follows the real font size and the bar's gap at every breakpoint.
 *
 * modules: [{ key, label, to, active, onClick }] — only the ones the user may see.
 */
const TopModuleNav = ({ modules, className }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(modules.length);
  const moduleKeys = modules.map((m) => m.key).join("|");
  const renderedModules = modules;

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return undefined;
    }

    const fit = () => {
      const style = getComputedStyle(container);
      const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
      const available =
        container.clientWidth -
        (parseFloat(style.paddingLeft) || 0) -
        (parseFloat(style.paddingRight) || 0);

      const items = Array.from(measure.children);
      const moreWidth = items.pop()?.getBoundingClientRect().width || 0;
      const widths = items.map((el) => el.getBoundingClientRect().width);

      if (widths.length === 0) {
        setVisibleCount(0);
        return;
      }

      const total = widths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);
      if (total <= available) {
        setVisibleCount(widths.length);
        return;
      }

      // Room must be left for the three-dots toggle itself.
      let used = moreWidth;
      let count = 0;
      while (count < widths.length && used + gap + widths[count] <= available) {
        used += gap + widths[count];
        count += 1;
      }
      setVisibleCount(count);
    };

    fit();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [moduleKeys]);

  const inline = renderedModules.slice(0, visibleCount);
  const overflow = renderedModules.slice(visibleCount);
  const overflowActive = overflow.some((m) => m.active);

  const buttonClass = (active) => (active ? "top_menu_btn-select " : "top_menu_btn blue_text");

  return (
    <div ref={containerRef} className={className} style={{ position: "relative", minWidth: 0 }}>
      {inline.map((m) => (
        <div key={m.key}>
          <Link to={m.to}>
            <button className={buttonClass(m.active)} onClick={m.onClick}>
              {m.label}
            </button>
          </Link>
        </div>
      ))}

      {overflow.length > 0 && (
        <Dropdown>
          <Dropdown.Toggle
            as="button"
            className={buttonClass(overflowActive)}
            title="More modules"
            aria-label="More modules"
          >
            <BsThreeDotsVertical size="1.2em" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {overflow.map((m) => (
              <Dropdown.Item
                key={m.key}
                as={Link}
                to={m.to}
                onClick={(event) => {
                  m.onClick?.(event);
                }}
                className={`custom-dropdown-item ${m.active ? "top-nav-dropdown-item-active active" : ""}`}
                // Bootstrap's own "active" fill is a bright blue that clashes with the
                // bar, so the current module is marked the way the bar marks it.
                style={
                  m.active
                    ? { color: "var(--top-menu-btn-active-text, #056363)", fontWeight: 600 }
                    : undefined
                }
              >
                {m.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}

      {/* Measuring copy: every module plus the toggle, laid out but never seen. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          whiteSpace: "nowrap",
          top: 0,
          left: 0,
        }}
      >
        {renderedModules.map((m) => (
          <button key={m.key} tabIndex={-1} className={buttonClass(m.active)}>
            {m.label}
          </button>
        ))}
        <button tabIndex={-1} className={buttonClass(false)}>
          <BsThreeDotsVertical size="1.2em" />
        </button>
      </div>
    </div>
  );
};

export default TopModuleNav;
